import api from '@/api';
import type { components } from '@/api/qraft/schema';
import {
  PEOPLE_PICKER_DEBOUNCE_MS,
  PEOPLE_PICKER_PAGE_SIZE,
} from '~/hooks/useAsyncPeoplePicker';

export type AssignCandidate = components['schemas']['AssignCandidate'];

/** Legacy's select2 delay, re-exported so the modal and the hook agree. */
export { PEOPLE_PICKER_DEBOUNCE_MS };

export interface AssignCandidatesResult {
  candidates: AssignCandidate[];
  /** Total matching the current search, not the length of the page. */
  count: number;
  isFetching: boolean;
  /** A refusal from the endpoint's own gate — a 404 on an unassignable target. */
  error: unknown;
}

/**
 * The assign picker's roster: the ADMIN/MOD leaders of the group behind the
 * SELECTED target, minus the caller.
 *
 * The target is part of the request, not a detail of the student:
 * `AssignStudentInteractivePopup.php:64-75` derives the group from the posted
 * `campaign_id`/`web_ad_id` and echoes `[]` when neither is posted, and
 * `adoption_info.php:40,51` stamp
 * `data-reset-on-change="student-board:assign-study-helper"` on both target
 * controls so the picker re-runs whenever the target changes. Passing them as
 * query parameters gets that refetch for free — they are part of the query key —
 * and the server validates the posted group against the student's own claim set
 * rather than trusting it, which legacy does not (spec §8 X7).
 *
 * @param studentId the student the modal is open for, or `null` when none is.
 * @param campaignId the posted campaign, when the student has a matched one.
 * @param webAdId the posted web target, when there is no campaign.
 * @param search the debounced search term; `''` asks for the first page.
 */
export function useAssignCandidates(
  studentId: number | null,
  campaignId: number | undefined,
  webAdId: number | undefined,
  search: string,
): AssignCandidatesResult {
  const hasTarget = campaignId !== undefined || webAdId !== undefined;

  const { data, isFetching, error } = api.getAssignCandidates.useQuery(
    {
      path: { student_id: studentId ?? 0 },
      query: {
        campaign_id: campaignId,
        web_ad_id: webAdId,
        search: search === '' ? undefined : search,
        limit: PEOPLE_PICKER_PAGE_SIZE,
      },
    },
    // Same `retry: false` as `useStudentAdoptionInfo`, and for the same reason:
    // this endpoint answers a typed 404 when the posted target is not one the
    // caller may assign through, and that is an answer rather than a fault.
    { enabled: studentId !== null && hasTarget, retry: false },
  );

  return {
    candidates: data?.data ?? [],
    count: data?.count ?? 0,
    isFetching,
    error,
  };
}
